import { useState, useEffect } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { X, Send, Loader2, Eye, CalendarRange } from "lucide-react";

const firstOfPrevMonthISO = () => {
  const d = new Date(); d.setDate(1); d.setDate(d.getDate() - 1); d.setDate(1);
  return d.toISOString().slice(0, 10);
};
const lastOfPrevMonthISO = () => {
  const d = new Date(); d.setDate(1); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};
const fmtMoney = (n) => `$${Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Per-book settings + send modal for the Monthly Closing Packet.
 * Lets admin toggle the auto-send flag, set a recipient override, send right now,
 * and preview the branded PDF.
 */
const ClosingPacketModal = ({ open, onClose, book, lang, onUpdated }) => {
  const [enabled, setEnabled] = useState(false);
  const [override, setOverride] = useState("");
  const [start, setStart] = useState(firstOfPrevMonthISO());
  const [end, setEnd] = useState(lastOfPrevMonthISO());
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);

  useEffect(() => {
    if (open && book) {
      setEnabled(!!book.monthly_closing_enabled);
      setOverride(book.monthly_closing_to || "");
      setStart(firstOfPrevMonthISO());
      setEnd(lastOfPrevMonthISO());
      setSubject("");
      setMessage("");
    }
  }, [open, book]);

  if (!open || !book) return null;

  const previewPdf = async () => {
    try {
      const { data } = await api.get(`/back-office/books/${book.id}/closing-packet.pdf`, { params: { start, end }, responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Preview failed"); }
  };

  const saveSettings = async () => {
    setSavingToggle(true);
    try {
      const { data } = await api.patch(`/back-office/books/${book.id}`, {
        monthly_closing_enabled: enabled,
        monthly_closing_to: override || null,
      });
      onUpdated?.(data);
      toast.success(lang === "es" ? "Configuración guardada" : "Settings saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSavingToggle(false); }
  };

  const sendNow = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/back-office/books/${book.id}/closing-packet/send`, {
        to: override || undefined,
        subject: subject || undefined,
        message: message || undefined,
        start, end,
      });
      const note = data?.mocked
        ? (lang === "es" ? " (modo prueba — sin clave Resend)" : " (MOCKED — no Resend key configured)")
        : "";
      toast.success((lang === "es" ? `Paquete enviado a ${data.to}` : `Packet sent to ${data.to}`) + note);
      onUpdated?.({ ...book, last_closing_packet_at: new Date().toISOString(), last_closing_packet_to: data.to, last_closing_packet_period: `${start} – ${end}` });
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="closing-packet-modal">
      <div className="bg-white max-w-2xl w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <div>
            <div className="rn3-eyebrow"><CalendarRange className="w-3 h-3 inline mr-1" /> {lang === "es" ? "Paquete de Cierre Mensual" : "Monthly Closing Packet"}</div>
            <h3 className="font-serif-display text-2xl mt-1">{book.name}</h3>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Auto-send settings */}
          <section className="border border-[#E5E5E5] p-4">
            <div className="rn3-eyebrow mb-3">{lang === "es" ? "Envío Mensual Automático" : "Monthly Auto-Send"}</div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="mt-1" data-testid="closing-packet-enabled" />
              <div className="flex-1">
                <div className="font-medium text-sm">{lang === "es" ? "Enviar paquete cada mes al cliente" : "Email the closing packet to the client every month"}</div>
                <div className="text-xs text-[#737373] mt-1">{lang === "es" ? "Se incluirá en la ejecución mensual programada con el período del mes anterior." : "Will be included in the monthly run with the previous month's period."}</div>
              </div>
            </label>
            <div className="mt-4">
              <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Email de destino (opcional)" : "Recipient email (optional)"}</label>
              <input type="email" value={override} onChange={(e) => setOverride(e.target.value)} placeholder={book.client?.email || (lang === "es" ? "Por defecto: email del cliente vinculado" : "Default: linked client's email")} className="rn3-input w-full" data-testid="closing-packet-to" />
              <div className="text-xs text-[#737373] mt-1">{lang === "es" ? "Si está vacío, se usará el email del usuario cliente vinculado al libro." : "If empty, the email of the client user linked to this book is used."}</div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-[#F3EFE9]">
              {book.last_closing_packet_period && (
                <div className="mr-auto text-xs text-[#737373]">{lang === "es" ? "Último envío:" : "Last sent:"} <span className="font-medium">{book.last_closing_packet_period}</span> → {book.last_closing_packet_to}</div>
              )}
              <button type="button" onClick={saveSettings} disabled={savingToggle} className="rn3-btn-gold" data-testid="closing-packet-save">
                {savingToggle ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {lang === "es" ? "Guardar" : "Save"}
              </button>
            </div>
          </section>

          {/* Send-now */}
          <form onSubmit={sendNow} className="border border-[#E5E5E5] p-4 space-y-4">
            <div className="rn3-eyebrow">{lang === "es" ? "Enviar Ahora" : "Send Now"}</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Desde" : "Start"}</label>
                <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rn3-input w-full" data-testid="closing-packet-start" />
              </div>
              <div>
                <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Hasta" : "End"}</label>
                <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rn3-input w-full" data-testid="closing-packet-end" />
              </div>
            </div>
            <div>
              <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Asunto (opcional)" : "Subject (optional)"}</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className="rn3-input w-full" data-testid="closing-packet-subject" placeholder={`Monthly Closing Packet · ${book.name} · ${start} – ${end}`} />
            </div>
            <div>
              <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Mensaje (opcional)" : "Message (optional)"}</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="rn3-input w-full" data-testid="closing-packet-message" placeholder={lang === "es" ? "Mensaje personalizado para el cliente…" : "Custom note to your client…"} />
            </div>
            <div className="text-xs text-[#737373] border border-[#F3EFE9] bg-[#FDFBF7] px-3 py-2">
              {lang === "es" ? "El PDF incluye Estado de Resultados, Balance General, Flujo de Efectivo y un resumen de Cuentas por Cobrar — con tu marca." : "The PDF includes P&L, Balance Sheet, Cash Flow, and an AR aging snapshot — fully branded."}
            </div>
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#F3EFE9]">
              <button type="button" onClick={previewPdf} className="rn3-btn-gold" data-testid="closing-packet-preview">
                <Eye className="w-4 h-4" /> {lang === "es" ? "Vista Previa PDF" : "Preview PDF"}
              </button>
              <div className="flex items-center gap-2">
                <button type="button" onClick={onClose} className="text-sm text-[#737373] hover:text-[#0A0A0A] uppercase tracking-widest">{lang === "es" ? "Cancelar" : "Cancel"}</button>
                <button disabled={busy} className="rn3-btn-primary" data-testid="closing-packet-send">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {busy ? "…" : (lang === "es" ? "Enviar Paquete" : "Send Packet")}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClosingPacketModal;
